import { useEffect, useState } from "react"
import { GridColDef, GridRowModel } from "@mui/x-data-grid"
import { PersonDataType } from "../../../context/SelectedPersonContext"
import { useNavigate, useParams } from "react-router"
import { SearchBar, TableRemoveButton, TableStandard, TableToolBar, useManageModal } from "ui"
import { gql, useLazyQuery, useMutation } from "@apollo/client"
import { Box, Button, Paper } from "@mui/material"
import { UNASSIGN_LOCATION_PERSON_MUTATION } from "../../../features/graphql/mutation"
import { Add } from "@mui/icons-material"
import { canAddStudentAndStaff } from "ft-shared-graphql"
import usePersonAuthStore from "../../store/personAuthStore";

export const GET_PERSON_LOCATIONS = gql(`
    query getPersonLocations($personId:UUID!){
        person(id:$personId){
            locations{
                edges{
                    node{
                        id
                        name
                    }
                }
            }
            singleStudents{
                        id
                        locationId
                        references
            }
            
        }
    }`
);

const mapLocationToTable = (data: any) =>
    data?.locations?.edges?.map((locationEdge: any) => {
        const location = locationEdge.node;
        return {
            id: location.id,
            name: location.name,
            students:
                data?.singleStudents
                    ?.filter(
                        (student: any) =>
                            student.locationId === location.id
                    )
                    .map((e: any) => e.node) || [],
        };
    });
const TABLE_COLUMNS: GridColDef[] = [
    { field: "name", headerName: "Location Name", flex: 1, sortable: true, resizable: false },

]
const StaffLocationTable = ({ selectedPerson }: { selectedPerson: PersonDataType | null }) => {
    const [pageTableState, setPageTableState] = useState<{ columns: GridColDef[], rows: GridRowModel[] }>({
        columns: [],
        rows: []
    })
    const { pid } = useParams();
    const navigate = useNavigate()
    const { openModal } = useManageModal()
    const [removeId, setRemoveId] = useState<string | null>()
    const [unassignPersons] = useMutation(UNASSIGN_LOCATION_PERSON_MUTATION, {
        refetchQueries: [{
            query: GET_PERSON_LOCATIONS,
            variables: { personId: pid }
        }]
    })
    const roles = usePersonAuthStore(state => state.roles)

    const [fetching, { data, loading, error, fetchMore }] = useLazyQuery(GET_PERSON_LOCATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            personId: pid
        }
    })
    useEffect(() => {
        async function fetchRecords() {
            await fetching()
        }
        fetchRecords();
    }, [])
    const handleRemoveLocation = (locationId: string) => {
        unassignPersons({
            variables: {
                locationId: locationId,
                personIds: [pid]
            }
        })
    }

    useEffect(() => {
        if (!selectedPerson) return;
        if (data) {
            setPageTableState((prevState) => {
                const _columns = [...TABLE_COLUMNS]
                if (canAddStudentAndStaff(roles)) {
                    _columns.push({
                        field: "remove", headerName: "Remove", headerAlign: "center", align: "center", sortable: false, width: 100,
                        renderCell: (params: any) => {
                            const disabled = params.row?.students.length > 0
                            return (
                                <TableRemoveButton
                                    id={params.row.id}
                                    disabled={disabled}
                                    removing={params.row.id === removeId}
                                    onRemove={handleRemoveLocation}
                                    onDisabled={() => {
                                        if (disabled) {
                                            return openModal({ type: "ONLY_STUDENT_REF" })
                                        }
                                        return openModal({ type: "UNKNOWN" })
                                    }}
                                />
                            )
                        }
                    })
                }
                const _rows = mapLocationToTable(data?.person);
                return {
                    ...prevState,
                    columns: _columns,
                    rows: _rows
                }
            })
        }
    }, [data, loading])
    const handleAddLocation = () => {
        navigate(`/manage/users/location/${pid}`)
    }
    return (
        <Paper component="div" variant="outlined" >
            <TableStandard
                loading={false}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <Box component="div">
                            <SearchBar
                                value={searchQuery}
                                clearSearch={onSearchClear}
                                onChange={onSearch}>
                                <Button
                                    color="secondary"
                                    sx={{ display: canAddStudentAndStaff(roles) ? "block" : "none" }}
                                    onClick={handleAddLocation}
                                    startIcon={
                                        <Add
                                            style={{
                                                backgroundColor: "#253D93",
                                                borderRadius: "100%",
                                                color: "white",
                                            }}
                                        />
                                    }
                                >
                                    Assign Locations
                                </Button>
                            </SearchBar>
                        </Box>

                    )
                }}
                rows={pageTableState.rows}
                columns={pageTableState.columns}
                pageSize={6}
                disableBoxShadow
            />
        </Paper>

    )
}

export default StaffLocationTable